import { useParams } from 'react-router-dom';
import { isMobileDevice, isNonWindowsOS } from '../../utility/utility';
import { download, setAutoDownloadFlag } from '../../utility/api';
import { useState, useEffect } from 'react';
import { usePavilion } from '../contexts/PavilionContext';
import { SignedIn, SignedOut, SignInButton, useUser, useAuth } from '@clerk/clerk-react';
import { Helmet } from 'react-helmet-async';
import { getPersistentPlayerId } from '../../api';
import { PlaycastAnalyticsUserStartedPavilionSignup, sendAnalytics } from '@playcastdotio/Analytics';
import { notifyMobileInstallReminder } from '../../api/modules/notifications';
import config from '../../config.json';

// Local storage key for email popup flag
const EMAIL_POPUP_FLAG = 'playcast-show-email-popup';

const images = [
  '/gangbeasts.jpg',
  '/horizon.jpg',
  '/tlou.jpg',
  '/subnautica.jpg',
  '/speedrunners.jpg',
  '/stickfight.jpg',
  '/castlecrashers.jpg',
  '/cuphead.jpg',
];

const structuredData = {
  '@context': 'http://schema.org',
  '@type': 'Organization',
  name: 'Playcast Pavilion',
  url: 'https://pavilion.playcast.io',
  logo: 'https://playcast.io/images/playcast_logo-96x96.png',
  contactPoint: [
    {
      '@type': 'ContactPoint',
      telephone: '+1-800-555-5555',
      contactType: 'Customer Service',
      areaServed: 'US',
      availableLanguage: 'English',
    },
  ],
  sameAs: [
    'https://discord.gg/7PYGxU68WH',
    'https://www.youtube.com/channel/UC-k16u9Ea_dvRhB70f-h5lQ',
    'https://www.twitch.tv/playcastio',
    'https://www.tiktok.com/@playcastdotio',
    'https://www.instagram.com/playcastio/',
  ],
};

// Only procs for when user is signed out
const SendHostAcquisitionInitiated = () => {
  sendAnalytics({
    name: 'User Started Pavilion Signup',
    role: 'host',
    action: 'button',
    persistentId: getPersistentPlayerId(),
  } as PlaycastAnalyticsUserStartedPavilionSignup);
};

export const AffiliateLanding = () => {
  const { affiliate } = useParams();
  const { isPavilionMode } = usePavilion();
  const { user } = useUser();
  const { isSignedIn, isLoaded } = useAuth();
  const affie = affiliate ? affiliate.charAt(0).toUpperCase() + affiliate.slice(1) : 'Your Favorite Streamer';
  const [index, setIndex] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(true);
  const [showEmailPopup, setShowEmailPopup] = useState(false);

  const clonedImages = [...images, images[0]];

  // Handler for mobile reminder email when signed in
  const handleMobileReminder = () => {
    notifyMobileInstallReminder(true, user?.primaryEmailAddress?.emailAddress);
    setShowEmailPopup(true);
  };

  // Enhanced handler that sets flag for popup after sign-in flow
  const handleSignUpClick = () => {
    SendHostAcquisitionInitiated();
    // Only set autodownload flag for Windows desktop users (not mobile or non-Windows)
    if (!isMobileDevice() && !isNonWindowsOS()) {
      setAutoDownloadFlag(false); // false = not mobile (Windows desktop)
    } else {
      // Set popup flag for mobile and non-Windows users - Windows desktop users get download instead
      localStorage.setItem(EMAIL_POPUP_FLAG, 'true');
    }
  };

  useEffect(() => {
    window.scrollTo(0, 0);
  });

  useEffect(() => {
    setIndex(1);
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      setIndex((prev) => prev + 1);
    }, 6000);

    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (index === clonedImages.length - 1) {
      setTimeout(() => {
        setIsTransitioning(false);
        setIndex(0);
      }, 50);
    } else {
      setIsTransitioning(true);
    }
  }, [index, clonedImages.length]);

  // Check for email popup flag after sign-in
  useEffect(() => {
    if (isLoaded && isSignedIn && user) {
      const shouldShowPopup = localStorage.getItem(EMAIL_POPUP_FLAG) === 'true';
      if (shouldShowPopup) {
        // Send mobile reminder email for anyone who signed in via the sign-up button
        if (isMobileDevice()) {
          notifyMobileInstallReminder(true, user.primaryEmailAddress?.emailAddress);
        }
        setShowEmailPopup(true);
        // Clear the flag since we're showing the popup
        localStorage.removeItem(EMAIL_POPUP_FLAG);
      }
    }
  }, [isLoaded, isSignedIn, user]);

  return (
    <>
    <Helmet>
        <title>Playcast Pavilion</title>
        <meta
          name='description'
          content={`Unlock free Steam games with ${affie}'s affiliate link!`}
        />
        <script type='application/ld+json'>{JSON.stringify(structuredData)}</script>
      </Helmet>

    <div className='bg-content text-content-text m-0 h-auto w-full p-0'>
      {/* Landing Greeting */}
      <div className="relative w-full overflow-hidden bg-[#2C354C] bg-[url('/Gibber-Texture.png')] bg-cover bg-bottom bg-no-repeat pb-[60px] pt-[60px]">
        {/* Background Carousel */}
        <div className="absolute inset-0 z-0 flex items-center justify-center md:justify-center lg:justify-end">
          <div className="relative w-full md:w-full lg:w-[60%] mr-0 md:mr-0 lg:mr-[3%] overflow-hidden">
            {/* Gradient overlays for smooth edges - only on large screens */}
            <div className="pointer-events-none absolute left-0 top-0 z-10 h-full w-56 bg-gradient-to-r from-[#2C354C] to-transparent hidden lg:block" />
            <div className="pointer-events-none absolute right-0 top-0 z-10 h-full w-48 bg-gradient-to-l from-[#2C354C] to-transparent hidden lg:block" />

            {/* Continuous scrolling container */}
            <div
              className="flex opacity-30"
              style={{
                width: `${images.length * 2 * 320}px`,
                animation: `scroll-left ${images.length * 6}s linear infinite`,
              }}
            >
              {/* First set of images */}
              {images.map((src, index) => (
                <div key={`bg-first-${index}`} className="flex-shrink-0 px-3">
                  <div className="relative overflow-hidden rounded-lg">
                    <img
                      src={src}
                      alt=""
                      className="aspect-[3/4] w-[290px] object-cover"
                      draggable={false}
                    />
                  </div>
                </div>
              ))}
              {/* Duplicate set for seamless loop */}
              {images.map((src, index) => (
                <div key={`bg-second-${index}`} className="flex-shrink-0 px-3">
                  <div className="relative overflow-hidden rounded-lg">
                    <img
                      src={src}
                      alt=""
                      className="aspect-[3/4] w-[290px] object-cover"
                      draggable={false}
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* CSS animation styles for background carousel */}
        <style dangerouslySetInnerHTML={{
          __html: `
            @keyframes scroll-left {
              0% {
                transform: translateX(0);
              }
              100% {
                transform: translateX(-${images.length * 320}px);
              }
            }
          `
        }} />

        {/* Content layer - positioned above background carousel */}
        <div className='relative z-10 flex w-full flex-col gap-8 sm:gap-10 md:flex-row md:gap-6 lg:gap-10'>
          {' '}
          {/* Added gap between stacked columns */}
          {/* Left Column — 50% width (was right column) */}
          <div className='flex w-full flex-col items-start justify-center gap-6 md:w-1/2 lg:w-1/2 order-2 md:order-1'>
            {/* Text and Button Container - centered on small, left aligned on larger */}
            <div className='w-full px-6 md:px-8 lg:px-0 lg:pl-[100px] flex justify-center md:justify-start'>
              <div className='inline-block space-y-6 text-white'>
                <div className='font-inter text-center md:text-left text-[50px] font-black sm:text-[60px] md:text-[70px] lg:text-[90px] leading-none'>
                  Get 100 FREE
                  <br />
                  Steam Games
                </div>
                <div className='font-inter text-center md:text-left'>
                  <p className='w-full max-w-[400px] sm:max-w-[500px] md:max-w-[600px] lg:max-w-[700px] text-[20px] font-normal italic sm:text-[22px] lg:text-[25px]'>
                    Become a Playcast Pavilion Host and join the gaming revolution. <br /> <br />
                  </p>
                  <p className='text-[20px] font-normal italic sm:text-[22px] lg:text-[25px]'>
                    <span className='text-[22px] font-bold not-italic sm:text-[25px] lg:text-[30px]'>Join the Network</span>
                  </p>
                </div>

                {/* Banner — centered on small, left aligned on larger */}
                <div className='mt-6 flex justify-center md:justify-start'>
                  <SignedIn>
                    {isMobileDevice() ? (
                      <div className='w-[200px] sm:w-[220px] md:w-[240px] lg:w-[240px] cursor-pointer' onClick={handleMobileReminder}>
                        <img
                          src='/signupnow.svg'
                          alt='Playcast Pavilion signup'
                          className='block h-full w-full object-cover'
                        />
                      </div>
                    ) : (
                      <div onClick={() => download()} className='w-[200px] sm:w-[220px] md:w-[240px] lg:w-[240px] cursor-pointer'>
                        <img src='/download footer.svg' alt='download banner' className='block h-full w-full object-cover' />
                      </div>
                    )}
                  </SignedIn>
                  <SignedOut>
                    <SignInButton mode='modal'>
                      <div className='w-[200px] sm:w-[220px] md:w-[240px] lg:w-[240px] cursor-pointer' onClick={handleSignUpClick}>
                        <img src={isMobileDevice() ? '/signupnow.svg' : '/download footer.svg'} alt='Sign in to download' className='block h-full w-full object-cover' />
                      </div>
                    </SignInButton>
                  </SignedOut>
                </div>
              </div>
            </div>
          </div>
          {/* Right Column — 50% width (was left column) */}
          <div className='flex w-full flex-col items-center justify-center gap-4 md:w-1/2 lg:w-1/2 order-1 md:order-2'>
            <div className='flex w-full flex-col items-center gap-4 pl-6 md:pl-12 lg:pl-[120px] pr-6 md:pr-8 lg:pr-[100px]'>
              <img src='/GradientLogo.svg' className='h-auto w-[180px] sm:w-[220px] md:w-[260px] lg:w-[346px] flex-shrink-0' alt='gradientlogo' />
              <img
                src='/PlaycastPavilionText.svg'
                className='inline-block h-auto w-[300px] object-contain sm:w-[380px] md:w-[420px] xl:w-[461px] flex-shrink-0'
                alt='Playcast Pavilion'
              />
            </div>
          </div>
        </div>
      </div>

      {/* Why Become a Pavilion Host? */}
      <div className='flex w-full flex-wrap items-start justify-center gap-12 bg-[#111827] px-6 py-20 sm:px-12 lg:px-32'>
        {/* Header Block */}
        <div className='w-full text-center'>
          <div className='font-syne text-[32px] font-bold leading-tight text-[#FFD219] sm:text-[44px] md:text-[52px] lg:text-[60px]'>
            Why Become a <br /> Playcast Pavilion<sup className='text-decoration-none align-super text-[0.35em]'>TM</sup> Host?
          </div>
        </div>

        {/* Left Column - FAQ Questions */}
        <div className='flex h-auto w-full flex-col gap-6 md:w-[550px]'>
          <details className='bg-content row-auto rounded-lg border border-gray-600'>
            <summary className='row-auto px-6 py-5 text-lg italic text-white cursor-pointer'>What is a Playcast Pavilion Host?</summary>
            <div className='row-auto space-y-4 px-6 pb-6 pt-2'>
              <p className='text-white'>
                A Host is anyone who volunteers their gaming PC to the Pavilion network in exchange for free games, gift cards, and
                rewards.
              </p>
            </div>
          </details>

          <details className='bg-content row-auto rounded-lg border border-gray-600'>
            <summary className='row-auto px-6 py-5 text-lg italic text-white cursor-pointer'>What is a Playcast Pavilion Kiosk?</summary>
            <div className='row-auto space-y-4 px-6 pb-6 pt-2'>
              <p className='text-white'>
                A "Kiosk" is a secure, sandboxed PC environment that allows remote players to stream licensed games safely.
              </p>
            </div>
          </details>

          <details className='bg-content row-auto rounded-lg border border-gray-600'>
            <summary className='row-auto px-6 py-5 text-lg italic text-white cursor-pointer'>Is my PC safe?</summary>
            <div className='row-auto space-y-4 px-6 pb-6 pt-2'>
              <p className='text-white'>
                Yes. Players only stream the games you authorize. They never access your desktop or files
              </p>
            </div>
          </details>

          <details className='bg-content row-auto rounded-lg border border-gray-600'>
            <summary className='row-auto px-6 py-5 text-lg italic text-white cursor-pointer'>Do I need to leave my PC on 24/7?</summary>
            <div className='row-auto space-y-4 px-6 pb-6 pt-2'>
              <p className='text-white'>
                Nope—you can schedule host times or enable async hosting during idle hours
              </p>
            </div>
          </details>
        </div>

        {/* Right Column - FAQ Questions */}
        <div className='flex h-auto w-full flex-col gap-6 md:w-[550px]'>
          <details className='bg-content row-auto rounded-lg border border-gray-600'>
            <summary className='row-auto px-6 py-5 text-lg italic text-white cursor-pointer'>Why can't I stream any Steam game with Playcast Pavilion?</summary>
            <div className='row-auto space-y-4 px-6 pb-6 pt-2'>
              <p className='text-white'>
                Only games with Steam Café licenses or developer approval can be hosted in Playcast Pavilion. This ensures full compliance
                with Steam's public performance policies.
              </p>
            </div>
          </details>

          <details className='bg-content row-auto rounded-lg border border-gray-600'>
            <summary className='row-auto px-6 py-5 text-lg italic text-white cursor-pointer'>Do I get access to free games as a Host?</summary>
            <div className='row-auto space-y-4 px-6 pb-6 pt-2'>
              <p className='text-white'>
                Yes! Hosts receive access to a licensed catalog of games in exchange for contributing idle PC time to the network.
              </p>
            </div>
          </details>

          <details className='bg-content row-auto rounded-lg border border-gray-600'>
            <summary className='row-auto px-6 py-5 text-lg italic text-white cursor-pointer'>Can't find your free games?</summary>
            <div className='row-auto space-y-4 px-6 pb-6 pt-2'>
              <p className='text-white'>
                The Playcast App requires its systems running in the background to operate, reopening the app should fix this! The app will still work when minimized!
              </p>
            </div>
          </details>

          <details className='bg-content row-auto rounded-lg border border-gray-600'>
            <summary className='row-auto px-6 py-5 text-lg italic text-white cursor-pointer'>Still have questions?</summary>
            <div className='row-auto space-y-4 px-6 pb-6 pt-2'>
              <p className='text-white'>
                Email us at{' '}
                <a href='mailto:support@playcast.io' className='text-blue-500 hover:underline'>
                  support@playcast.io
                </a>{' '}
                or join our{' '}
                <a
                  href={config.DISCORD_URL}
                  className='text-blue-500 hover:underline'
                  target='_blank'
                  rel='noopener noreferrer'
                >
                  Discord
                </a>{' '}
                to chat with the team.
              </p>
            </div>
          </details>
        </div>
      </div>

      {/* Email Confirmation Popup */}
      {showEmailPopup && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="relative mx-4 max-w-md rounded-lg bg-white p-6 shadow-xl">
            {/* Close button */}
            <button
              onClick={() => {
                setShowEmailPopup(false);
                // Clear localStorage flag as safety measure
                localStorage.removeItem(EMAIL_POPUP_FLAG);
              }}
              className="absolute right-3 top-3 flex h-8 w-8 items-center justify-center rounded-full text-gray-600 hover:text-gray-800 transition-colors"
              aria-label="Close popup"
            >
              <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>

            {/* Content */}
            <div className="pr-8">
              <h3 className="text-xl font-bold text-gray-900 mb-4">Welcome to Playcast Pavilion!</h3>
              <p className="text-gray-700 leading-relaxed">
                An email with download information has been sent. Make sure to check your spam folder if you can't find it.
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
    </>
  );
};
